<template>
  <div style="width: 100%">
    <!-- note- ^^this unlabeled div does not exist in brig-->
    <span v-if="closeButtonEnabled" class="rfmodal-close inline-flex flex-row-reverse">
      <v-icon v-if="!hideCloseButton" id="rfmodal-close-icon" :style="{ color: fillColor }">
        md:close
      </v-icon>
      <RfPromptTimeout
        v-if="isNewDeviceEditor || newTimeout"
        :actions="actions"
        class="my-auto mr-2"
        newVariant
        :mobileWeb="newTimeout"
      />
    </span>

    <template v-if="isVideo">
      <div class="video-volume-control">
        <span class="video-volume-up">
          <v-icon
            v-if="actions.rf_settings_video_muted === 'true'"
            size="24"
            :style="{ color: fillColor }"
          >
            md:volume_off
          </v-icon>
          <v-icon v-else size="24" class="video-volume-up" :style="{ color: fillColor }"
            >md:volume_up</v-icon
          >
        </span>
      </div>
      <span class="rfmodal-play">
        <v-icon
          v-if="actions.rf_settings_video_autoplayed !== 'true'"
          size="24"
          :style="{ color: fillColor }"
        >
          fa-play
        </v-icon>
      </span>
    </template>
    <div class="rfmodal-inner-wrapper">
      <div :class="backgroundContentAlignmentClass">
        <div
          class="rfmodal-text-container"
          :class="messageAlignClass"
          :style="{ width: actions.rf_settings_text_container_max_width }"
        >
          <div>
            <div>
              <!-- extra div layers to match brig ^^ -->
              <div class="rfmodal-header">
                <h1 :style="titleStyle" v-html="parsedTitle"></h1>
              </div>
              <div class="rfmodal-body">
                <RfBodyCountdown :actions="actions" />
                <div
                  class="rfmodal-message"
                  :style="messageStyle"
                  v-html="isNewDeviceEditor && isMobile ? mobileMessage : message"
                ></div>
              </div>
            </div>
          </div>
          <RfPromoBillingPreview v-if="hasBillingForm" :actions="actions" />
          <RfPromoSurveyPreview
            v-else-if="hasSurvey"
            :actions="actions"
            :deviceType="model.device_type"
          />
          <RfPromoInputsPreview v-else :actions="actions" />
          <RfPromoConsentPreview
            v-if="consent.needsConsent.value"
            v-model="consent.consentChecked.value"
            :actions="actions"
          />

          <div
            class="rfmodal-footer"
            :class="{
              'disable-bg':
                !actions.rf_retention_button1_text &&
                !(
                  actions.rf_settings_confirm_button_2_enabled === 'true' &&
                  actions.rf_retention_button2_text
                ) &&
                !(
                  actions.rf_settings_cancel_button_enabled === 'true' &&
                  actions.rf_retention_button3_text
                ),
            }"
          >
            <RfRetentionMessageButton
              :actions="actions"
              :model="model"
              :newTimeout="newTimeout"
              :isNewDeviceEditor="isNewDeviceEditor"
              :acceptButtonEnabled="consent.consentNotNeededOrGiven.value"
              :newButtonStyles="newButtonStyles"
            />
          </div>
          <RfRetentionLinksPreview
            :fillColor="actions.rf_settings_fill_color"
            :text="actions?.rf_settings_privacy_policy_text"
          />
        </div>
        <div v-if="!isVideo" :style="columnspacerWidth" class="rfmodal-colm-spacer"></div>
      </div>
    </div>
  </div>
</template>

<script>
import PromoPreviewMixin from "@/utils/PromoPreviewMixin";
import RfRetentionLinksPreview from "@/components/RfPathCreate/RfRetentionActions/components/RfRetentionLinksPreview.vue";
import RfRetentionMessageButton from "@/components/RfPathCreate/RfRetentionActions/components/RfRetentionMessageButton.vue";
import RfPromoInputsPreview from "@/components/RfPathCreate/RfRetentionActions/components/RfPromoInputsPreview.vue";
import RfPromoBillingPreview from "@/components/RfPathCreate/RfRetentionActions/components/RfPromoBillingPreview.vue";
import RfPromoSurveyPreview from "@/components/RfPathCreate/RfRetentionActions/components/RfPromoSurveyPreview.vue";
import RfPromoConsentPreview from "@/components/RfPathCreate/RfRetentionActions/components/RfPromoConsentPreview.vue";
import RfPromptTimeout from "./RfPromptTimeout.vue";
import RfBodyCountdown from "../RfBodyCountdown.vue";
import { useConsentPreview } from "@/utils/composables/useConsentPreview";
import { useAppsStore } from "@/pinia/appsStore";
import { useLiquidContextStore } from "@/pinia/liquidContextStore";

export default {
  name: "RfRetentionMessage",
  components: {
    RfRetentionLinksPreview,
    RfRetentionMessageButton,
    RfPromoBillingPreview,
    RfPromoInputsPreview,
    RfPromoSurveyPreview,
    RfPromoConsentPreview,
    RfPromptTimeout,
    RfBodyCountdown,
  },
  mixins: [PromoPreviewMixin],
  props: {
    model: { type: Object, default: null },
    actions: { type: Object, default: null },
    isVideo: { type: Boolean, default: false },
    hideCloseButton: { type: Boolean, default: false },
    isNewDeviceEditor: { type: Boolean, default: false },
    newTimeout: { type: Boolean, default: false },
    isMobile: { type: Boolean, default: false },
    newButtonStyles: { type: Boolean, default: false },
  },
  setup: props => ({
    consent: useConsentPreview(props),
    appsStore: useAppsStore(),
    liquidContextStore: useLiquidContextStore(),
  }),
  computed: {
    parsedTitle() {
      const titleKey =
        this.isNewDeviceEditor && this.isMobile ? "rf_mobile_title" : "rf_retention_title";
      const template = this.actions?.[titleKey];

      return this.liquidContextStore.liquidify(template);
    },
    messageStyle() {
      return {
        "font-size":
          this.isNewDeviceEditor && this.isMobile
            ? this.actions.rf_settings_mobile_message_font_size
            : this.actions.rf_settings_message_font_size,
        "color": this.fillColor,
      };
    },
    titleStyle() {
      return {
        "font-size":
          this.isNewDeviceEditor && this.isMobile
            ? this.actions.rf_settings_mobile_title_font_size
            : this.actions.rf_settings_title_font_size,
        "color": this.fillColor,
        "margin-top":
          this.isNewDeviceEditor && this.isMobile
            ? this.actions.rf_settings_mobile_title_padding_top
            : this.actions.rf_settings_title_padding_top,
      };
    },
    messageAlignClass() {
      if (this.actions.rf_retention_align) {
        return `${this.actions.rf_retention_align}-align-mode`;
      }
      return "";
    },
    backgroundContentAlignmentClass() {
      if (this.actions.rf_retention_img_align === "left") {
        return "rfmodal-content-wrapper b-left-align";
      }
      return "rfmodal-content-wrapper b-right-align";
    },
    hasBillingForm() {
      return `${this.actions.rf_billing_update_enabled}` === "true";
    },
    hasSurvey() {
      return `${this.actions.rf_retention_survey_selected}` === "true";
    },
    columnspacerWidth() {
      return {
        width: `calc(100% - ${this.actions.rf_settings_text_container_max_width})`,
        height: "4px",
      };
    },
  },
};
</script>

<style scoped>
.video-volume-control {
  position: absolute;
  bottom: 20px;
  right: 20px;
}
.rfmodal-play {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}
</style>
